import type { PrinterDestination } from '@biteinc/enums';
import { EpsonBarcodeFormat, EpsonBarcodeFormatHelper } from '@biteinc/enums';

type TableRowText = [command: PrintJob.EposCmd.addText, ...columns: string[][]];
type TableRowLineBreak = [command: PrintJob.EposCmd.addFeedLine, width: 1];

interface Table {
  rows: (TableRowText | TableRowLineBreak)[];
  columnCount: number;
  spacings: string[];
  alignments: PrintJob.Epos2Align[];
}

function isElementNode(node: Node): node is Element {
  return node.nodeType === 1 && typeof (node as Element).getAttribute === 'function';
}

abstract class PrintJob {
  private commands: PrintJob.Command[] = [];

  private textSize: number = 1;

  private textFont: PrintJob.Epos2Font = PrintJob.Epos2Font.EPOS2_FONT_A;

  private table?: Table;

  constructor(protected readonly printerSeries?: PrintJob.Epos2PrinterSeries) {}

  addNewLine(): this {
    this.commands.push([PrintJob.EposCmd.addFeedLine, 1]);
    return this;
  }

  addSound(): this {
    this.commands.push([PrintJob.EposCmd.addSound]);
    return this;
  }

  addUnbreakableText(text: string): this {
    this.commands.push([PrintJob.EposCmd.addText, text]);
    return this;
  }

  addText(text: string): this {
    this.addTextWithSecondLineIndent(text);
    return this;
  }

  addTextWithSecondLineIndent(text: string, secondLineIndent: string = ''): this {
    if (!this.printerSeries) {
      this.commands.push([PrintJob.EposCmd.addText, text, secondLineIndent]);
      return this;
    }

    const lines = PrintJob.linesFromText(
      text,
      secondLineIndent,
      this.getPrinterCharLimit(),
      this.getMaxWordLength(),
    );
    for (let i = 0; i < lines.length; i++) {
      if (i > 0) {
        this.addNewLine();
      }
      if (lines[i].length) {
        this.commands.push([PrintJob.EposCmd.addText, lines[i]]);
      }
    }
    return this;
  }

  addRepeatedText(text: string): this {
    if (!this.printerSeries) {
      this.commands.push([PrintJob.EposCmd.addRepeatedText, text]);
      return this;
    }

    let string = '';
    let maxLength = this.getMaxWordLength();
    while (maxLength > 0) {
      if (text.length > maxLength) {
        string += text.substr(0, maxLength);
        maxLength = 0;
      } else {
        string += text;
        maxLength -= text.length;
      }
    }
    this.addText(string);
    return this;
  }

  addTextStyle(reverse: number, ul: number, em: number, color: PrintJob.Epos2Color): this {
    this.commands.push([PrintJob.EposCmd.addTextStyle, reverse, ul, em, color]);
    return this;
  }

  addImage(base64ImageData: string, width: number, height: number): this {
    const xCoordinate = 0;
    const yCoordinate = 0;
    const color = 1;
    const mode = 3;
    const halftone = 3;
    const brightness = 1;
    const compress = 1;
    this.commands.push([
      PrintJob.EposCmd.addImage,
      base64ImageData,
      xCoordinate,
      yCoordinate,
      width,
      height,
      color,
      mode,
      halftone,
      brightness,
      compress,
    ]);
    return this;
  }

  align(alignment: PrintJob.Epos2Align): this {
    this.commands.push([PrintJob.EposCmd.addTextAlign, alignment]);
    return this;
  }

  setTextSize(textSize: PrintJob.TextSize): this {
    this.textSize = Math.min(textSize, PrintJob.getMaxTextSizeForPrinterSeries(this.printerSeries));
    this.commands.push([PrintJob.EposCmd.addTextSize, this.textSize, this.textSize]);
    return this;
  }

  setFont(font: PrintJob.Epos2Font): this {
    this.textFont = font;
    this.commands.push([PrintJob.EposCmd.addTextFont, font]);
    return this;
  }

  /**
   * Sets the default body font for a receipt, chosen per printer. SNBC BTP-S80
   * renders Font B too small and tight at 203 dpi, so it uses Font A (12x24,
   * 53 cols @ 80mm); Epson and built-in printers use the more compact Font B.
   *
   * Font choice and column width (getCharLimitForPrinterSeriesAndFont) must
   * agree — pick one without the other and right-aligned columns drift off the
   * margin. Centralizing both here keeps them in lockstep, so downstream byte
   * emitters (e.g. the Android ESC-POS translator) don't need their own
   * per-printer font override.
   */
  setReceiptBodyFont(): this {
    const bodyFont =
      this.printerSeries === PrintJob.Epos2PrinterSeries.SNBC_BTP_S80
        ? PrintJob.Epos2Font.EPOS2_FONT_A
        : PrintJob.Epos2Font.EPOS2_FONT_B;
    return this.setFont(bodyFont);
  }

  cut(): this {
    this.commands.push([PrintJob.EposCmd.addCut, PrintJob.Epos2Cut.EPOS2_CUT_FEED]);
    return this;
  }

  private getPrinterCharLimit(): number {
    const charLimit = PrintJob.getCharLimitForPrinterSeriesAndFont(
      this.printerSeries,
      this.textFont,
    );
    return Math.floor(charLimit / this.textSize);
  }

  private getMaxWordLength(): number {
    // Under higher font sizes on FontA, the printer has a limit of how long a
    // single word can be
    let delta = 0;
    if (PrintJob.Epos2Font.EPOS2_FONT_A === this.textFont && this.textSize > 1) {
      delta = this.textSize;
    }
    return this.getPrinterCharLimit() - delta;
  }

  addBarcode(string: string, barcodeType: EpsonBarcodeFormat, hri: PrintJob.Epos2Hri): this {
    this.commands.push([
      PrintJob.EposCmd.addBarcode,
      string,
      barcodeType,
      hri,
      -2, // font, default
      // width, Code128 has long barcodes and needs to be smaller width
      barcodeType === EpsonBarcodeFormat.CODE128 ? 2 : 3,
      -1, // height, unspecified
    ]);
    return this;
  }

  addQRCode(payload: string, size: number): this {
    const TypeQrCode = 3;
    const LevelM = 10;
    const width = size;
    const height = size;
    this.commands.push([
      PrintJob.EposCmd.addSymbol,
      payload,
      TypeQrCode,
      LevelM,
      width,
      height,
      size,
    ]);
    return this;
  }

  startNewTableCommand(
    columnCount: number,
    spacings: string[],
    alignments: PrintJob.Epos2Align[],
  ): this {
    this.table = {
      rows: [],
      columnCount,
      spacings,
      alignments,
    };
    return this;
  }

  addEmptyRow(): this {
    this.table!.rows.push([PrintJob.EposCmd.addFeedLine, 1]);
    return this;
  }

  addTextRow(strings: string[][]): this {
    this.table!.rows.push([PrintJob.EposCmd.addText, ...strings]);
    return this;
  }

  finishTableCommand(): this {
    const kMaxLength = this.getPrinterCharLimit();
    const kMaxWordLength = this.getMaxWordLength();
    const table = this.table!;

    // Figure out the width for each column starting from the right; the first
    // column will get all the remaining space
    const columnWidths: number[] = [];
    for (let a = 0; a < table.columnCount; a++) {
      columnWidths.push(0);
    }

    let remainingWidth = kMaxLength;
    for (let b = table.columnCount - 1; b >= 1; b--) {
      const columnWidth = PrintJob.getMaxWidthForColumn(b, table.rows);
      columnWidths[b] = columnWidth;
      remainingWidth -= columnWidth;
      remainingWidth -= table.spacings[b - 1].length;
    }
    columnWidths[0] = remainingWidth;

    table.rows.forEach((row) => {
      if (PrintJob.EposCmd.addFeedLine === row[0]) {
        // self.addNewLine(row.length > 1 ? row[1] : 1);
        this.addNewLine();
      } else if (PrintJob.EposCmd.addText === row[0]) {
        // Collect text for each cell
        let maxLineSetCount = 0;
        const lineSets: string[][] = [];
        for (let columnIndex = 0; columnIndex < table.columnCount; columnIndex++) {
          const column = row[columnIndex + 1];
          const text = column[0];
          const lineSet = PrintJob.linesFromText(
            text,
            column.length > 1 ? column[1] : '',
            columnWidths[columnIndex],
            kMaxWordLength,
          );
          lineSets.push(lineSet);
          maxLineSetCount = Math.max(maxLineSetCount, lineSet.length);
        }

        // Concatenate the cells and print them
        for (let i = 0; i < maxLineSetCount; i++) {
          if (i > 0) {
            // self.addNewLine(1);
            this.addNewLine();
          }

          let line = '';
          for (let j = 0; j < table.columnCount; j++) {
            if (j > 0) {
              line += table.spacings[j - 1];
            }
            const cellText = lineSets[j].length > i ? lineSets[j][i] : '';
            line += PrintJob.paddedStringToLength(cellText, columnWidths[j], table.alignments[j]);
          }

          if (line.length) {
            this.addText(line);
          }
        }
        this.addNewLine();
      }
    });

    this.table = undefined;
    return this;
  }

  protected processSizeChangeNode(
    node: Node,
    currentFontSize: PrintJob.TextSize,
    newFontSize: PrintJob.TextSize,
    isBold: boolean,
  ): this {
    this.setTextSize(newFontSize);
    node.childNodes.forEach((childNode) => {
      this.processNode(childNode, newFontSize, isBold);
    });
    this.setTextSize(currentFontSize);
    return this;
  }

  protected processNode(
    node: Node,
    currentFontSize: PrintJob.TextSize,
    isBold: boolean,
    isCondensedSpacing: boolean = false,
  ): this {
    if (node.nodeName === '#text') {
      const lines = node.textContent?.split('\n') || [];
      lines.forEach((line, index) => {
        if (line.length) {
          if (line === '{{ORDER_NUMBER}}') {
            this.addUnbreakableText(line);
          } else {
            this.addText(line);
          }
        }

        // 1. Vitrine: With isCondensedSpacing=true:
        //    Input: "Line 1\nLine 2\nLine 3"
        //    Output: "Line 1\nLine 2\nLine 3"
        //    (single newline between lines)
        //
        // 2. GCN: With isCondensedSpacing=false (default):
        //    Input: "Line 1\nLine 2\nLine 3"
        //    Output: "Line 1\n\nLine 2\n\nLine 3"
        //    (double newline between lines)
        //
        // 3. Empty lines are always preserved:
        //    Input: "Line 1\n\nLine 2"
        //    Output (both modes): "Line 1\n\nLine 2"
        if (isCondensedSpacing) {
          if (index < (lines.length || 0) - 1) {
            this.addNewLine();
          }
        } else if (!line.length) {
          this.addNewLine();
        }
      });
    } else if (node.nodeName === 'S3') {
      this.processSizeChangeNode(node, currentFontSize, 3, isBold);
    } else if (node.nodeName === 'S2') {
      this.processSizeChangeNode(node, currentFontSize, 2, isBold);
    } else if (node.nodeName === 'S1') {
      this.processSizeChangeNode(node, currentFontSize, 1, isBold);
    } else if (node.nodeName === 'B') {
      this.addTextStyle(0, 0, 1, PrintJob.Epos2Color.EPOS2_COLOR_1);
      node.childNodes.forEach((childNode) => {
        this.processNode(childNode, currentFontSize, true, isCondensedSpacing);
      });
      this.addTextStyle(0, 0, isBold ? 1 : 0, PrintJob.Epos2Color.EPOS2_COLOR_1);
    } else if (node.nodeName === 'QRCODE' && node.textContent && isElementNode(node)) {
      let sizeAttr = node.getAttribute('size');
      let size = sizeAttr ? parseInt(sizeAttr, 10) : 6;
      size = isNaN(size) ? 8 : Math.max(3, Math.min(size, 10));
      this.addQRCode(node.textContent.trim(), size);
    }
    return this;
  }

  getCommands(): PrintJob.Command[] {
    return this.commands;
  }

  static stringFromPrintJobPayload(payload: PrintJob.Payload, maxLineLength: number = 42): string {
    return payload.commands.reduce((printJobString, command) => {
      const [eposCmd, ...otherArguments] = command;
      switch (eposCmd) {
        case PrintJob.EposCmd.addText: {
          const text = otherArguments[0] as string;
          // const secondLineIndent = (otherArguments[1] as string || '');
          return `${printJobString}${text}\n`;
        }
        case PrintJob.EposCmd.addRepeatedText: {
          const text = otherArguments[0] as string;
          let string = '';
          let maxLength = maxLineLength;
          while (maxLength > 0) {
            if (text.length > maxLength) {
              string += text.substring(0, maxLength);
              maxLength = 0;
            } else {
              string += text;
              maxLength -= text.length;
            }
          }
          return `${printJobString}${string}\n`;
        }
        case PrintJob.EposCmd.addBarcode: {
          const barcodePayload = otherArguments[0] as string;
          const barcodeType = otherArguments[1] as EpsonBarcodeFormat;
          // we are not looking for localization here
          const barcodeName = EpsonBarcodeFormatHelper.name(barcodeType);
          return `${printJobString}${barcodeName} Barcode: ${barcodePayload}\n`;
        }
        case PrintJob.EposCmd.addSymbol: {
          const qrCodePayload = otherArguments[0] as string;
          const qrCodeType = otherArguments[1] as number;
          const qrCodeLevel = otherArguments[2] as number;
          const qrCodeSize = otherArguments[3] as number;
          return `${printJobString}QR Code (type: ${qrCodeType}; level: ${qrCodeLevel}; size: ${qrCodeSize}):\n${qrCodePayload}\n`;
        }
        case PrintJob.EposCmd.addFeedLine: {
          const lineBreakCount = otherArguments[0] as number;
          return `${printJobString}${'\n'.repeat(lineBreakCount)}`;
        }
        case PrintJob.EposCmd.addSound:
          return `${printJobString}SOUND!!! SOUND!!! SOUND!!!\n`;
        case PrintJob.EposCmd.addCut:
          return `${printJobString}CUT!!! CUT!!! CUT!!!\n`;
        default:
          return printJobString;
      }
    }, '');
  }
}

namespace PrintJob {
  export type Command = [PrintJob.EposCmd, ...(string | number)[]];

  export type Payload = {
    destination: PrinterDestination;
    commands: Command[];
  };

  export function getCharLimitForPrinterSeriesAndFont(
    series: PrintJob.Epos2PrinterSeries | undefined,
    font: PrintJob.Epos2Font,
  ): number {
    const FONT_B = PrintJob.Epos2Font.EPOS2_FONT_B;
    switch (series) {
      case PrintJob.Epos2PrinterSeries.EPOS2_TM_M30:
        return FONT_B === font ? 56 : 48;
      case PrintJob.Epos2PrinterSeries.EPOS2_TM_T88:
        return FONT_B === font ? 56 : 42;
      case PrintJob.Epos2PrinterSeries.EPOS2_TM_U220:
        return FONT_B === font ? 40 : 33;
      case PrintJob.Epos2PrinterSeries.SNBC_BTP_S80:
        // SNBC BTP-S80 spec: 80mm @ 203 dpi, full ~640-dot head.
        // Font A (12x24) = 53 cols, Font B (9x17) = 71 cols.
        return FONT_B === font ? 71 : 53;
      case PrintJob.Epos2PrinterSeries.SAMSUNG_BUILT_IN:
        // BK3-31 48mm print width: Font B (9x17) = 42 cols.
        // Vitrine receipts use Font B for built-ins via setReceiptBodyFont.
        return 42;

      // Assume T88 by default
      default:
        return 42;
    }
  }

  export type TextSize = 1 | 2 | 3 | 4 | 5;

  export function getMaxTextSizeForPrinterSeries(
    printerSeries?: PrintJob.Epos2PrinterSeries,
  ): TextSize {
    switch (printerSeries) {
      case PrintJob.Epos2PrinterSeries.EPOS2_TM_M30:
      case PrintJob.Epos2PrinterSeries.EPOS2_TM_T88:
      case PrintJob.Epos2PrinterSeries.SNBC_BTP_S80:
        return 5;
      case PrintJob.Epos2PrinterSeries.EPOS2_TM_U220:
        return 2;
      case PrintJob.Epos2PrinterSeries.SAMSUNG_BUILT_IN:
        // Samsung printers don't support changing text size
        return 1;
      default:
        return 3;
    }
  }

  export function linesFromText(
    text: string,
    secondLineIndent: string,
    maxLength: number,
    maxWordLength: number,
  ): string[] {
    const lines: string[] = [];

    const existingLines = text.split('\n');
    existingLines.forEach((curText: string) => {
      if (
        curText.length > maxLength ||
        (curText.length > maxWordLength && curText.indexOf(' ') <= 0)
      ) {
        const words = curText.split(' ');
        let line = '';
        for (let i = 0; i < words.length; i++) {
          let word = words[i];

          // If the word is empty that means there was a double space.
          // Let's preserve it.
          if (!word.length) {
            word = ' ';
          }

          const needsSpace = line.length > 0 && line.charAt(line.length - 1) !== ' ';
          if (
            line.length + word.length + (needsSpace ? 1 : 0) > maxLength ||
            word.length > maxWordLength
          ) {
            if (line.length) {
              lines.push(line);
              line = secondLineIndent;
            }

            if (word.length > maxWordLength) {
              let startIndex = 0;
              while (startIndex < word.length) {
                let subString = '';
                if (word.length - startIndex <= maxWordLength) {
                  subString = word.substr(startIndex);
                  startIndex = word.length;
                  line += subString;
                } else {
                  subString = `${word.substr(startIndex, maxWordLength - 1)}-`;
                  startIndex += maxWordLength - 1;
                  lines.push(subString);
                }
              }
            } else {
              line += word;
            }
          } else {
            line += (needsSpace ? ' ' : '') + word;
          }
        }

        if (line.length) {
          lines.push(line);
        }
      } else {
        lines.push(curText);
      }
    });

    return lines;
  }

  export function getMaxWidthForColumn(
    columnIndex: number,
    rows: (TableRowText | TableRowLineBreak)[],
  ): number {
    return rows.reduce((maxWidth, row) => {
      if (PrintJob.EposCmd.addText === row[0]) {
        const column = row[columnIndex + 1];
        const text = column[0];
        return Math.max(maxWidth, text.length);
      }
      return maxWidth;
    }, 0);
  }

  export function paddedStringToLength(
    string: string,
    length: number,
    alignment: PrintJob.Epos2Align,
  ): string {
    let paddedString = '';
    let spaceCountPre = 0;
    let spaceCountPost = 0;
    if (PrintJob.Epos2Align.EPOS2_ALIGN_LEFT === alignment) {
      spaceCountPost = length - string.length;
    } else if (PrintJob.Epos2Align.EPOS2_ALIGN_RIGHT === alignment) {
      spaceCountPre = length - string.length;
    } else {
      spaceCountPre = Math.floor((length - string.length) / 2);
      spaceCountPost = length - string.length - spaceCountPre;
    }
    for (let i = 0; i < spaceCountPre; i++) {
      paddedString += ' ';
    }
    paddedString += string;
    for (let j = 0; j < spaceCountPost; j++) {
      paddedString += ' ';
    }
    return paddedString;
  }

  type QrCodeConfig = {
    header?: string;
    itemDelimiter?: string;
    prefixFor6DigitPlus?: string;
    prefixFor12DigitPlus?: string;
    prefixFor39DigitPlus?: string;
    prefixForOtherPlus?: string;
    includeFreeMods?: boolean;
  };

  export function getSingleItemBarcodeBlock(barcode: string, qrCodeConfig: QrCodeConfig): string {
    if (barcode.length === 12 && qrCodeConfig.prefixFor12DigitPlus) {
      return `${qrCodeConfig.prefixFor12DigitPlus}${barcode}`; // UPC A
    }
    if (barcode.length === 6 && qrCodeConfig.prefixFor6DigitPlus) {
      return `${qrCodeConfig.prefixFor6DigitPlus}${barcode}`; // UPC E
    }
    if (barcode.length === 39 && qrCodeConfig.prefixFor39DigitPlus) {
      return `${qrCodeConfig.prefixFor39DigitPlus}${barcode}`; // Code 39
    }
    if (qrCodeConfig.prefixForOtherPlus) {
      return `${qrCodeConfig.prefixForOtherPlus}${barcode}`; // Code 128
    }
    return barcode;
  }

  export type QrCodeConfigOldImplementation = {
    qrCodeItemNeedsBarcodeTypePrefix?: boolean;
    qrCodePrefixFor6DigitPlu?: string;
    qrCodePrefixFor12DigitPlu?: string;
    qrCodePrefixFor39DigitPlu?: string;
    qrCodePrefixForRestOfPlu?: string;
  };

  export function convertNewConfigToOldImplementation(
    qrCodeConfig: QrCodeConfig,
  ): Required<QrCodeConfigOldImplementation> {
    return {
      qrCodeItemNeedsBarcodeTypePrefix:
        !!qrCodeConfig.prefixFor6DigitPlus ||
        !!qrCodeConfig.prefixFor12DigitPlus ||
        !!qrCodeConfig.prefixFor39DigitPlus ||
        !!qrCodeConfig.prefixForOtherPlus,
      qrCodePrefixFor6DigitPlu: qrCodeConfig.prefixFor6DigitPlus || '',
      qrCodePrefixFor12DigitPlu: qrCodeConfig.prefixFor12DigitPlus || '',
      qrCodePrefixFor39DigitPlu: qrCodeConfig.prefixFor39DigitPlus || '',
      qrCodePrefixForRestOfPlu: qrCodeConfig.prefixForOtherPlus || '',
    };
  }

  // Redefined enums from Epson's ePOS-Print.h.
  export enum Epos2Align {
    EPOS2_ALIGN_LEFT = 0,
    EPOS2_ALIGN_CENTER = 1,
    EPOS2_ALIGN_RIGHT = 2,
  }

  export enum Epos2Color {
    EPOS2_COLOR_NONE = 0,
    EPOS2_COLOR_1 = 1,
    EPOS2_COLOR_2 = 2,
    EPOS2_COLOR_3 = 3,
    EPOS2_COLOR_4 = 4,
  }

  export enum Epos2Font {
    EPOS2_FONT_A = 0,
    EPOS2_FONT_B = 1,
    EPOS2_FONT_C = 2,
    EPOS2_FONT_D = 3,
    EPOS2_FONT_E = 4,
  }

  export enum Epos2Hri {
    EPOS2_HRI_NONE = 0,
    EPOS2_HRI_ABOVE = 1,
    EPOS2_HRI_BELOW = 2,
    EPOS2_HRI_BOTH = 3,
  }

  export enum Epos2Cut {
    EPOS2_CUT_FEED = 0,
    EPOS2_CUT_NO_FEED = 1,
    EPOS2_CUT_RESERVE = 2,
  }

  export enum Epos2PrinterSeries {
    EPOS2_TM_M10 = 0,
    EPOS2_TM_M30 = 1,
    EPOS2_TM_P20 = 2,
    EPOS2_TM_P60 = 3,
    EPOS2_TM_P60II = 4,
    EPOS2_TM_P80 = 5,
    EPOS2_TM_T20 = 6,
    EPOS2_TM_T60 = 7,
    EPOS2_TM_T70 = 8,
    EPOS2_TM_T81 = 9,
    EPOS2_TM_T82 = 10,
    EPOS2_TM_T83 = 11,
    EPOS2_TM_T88 = 12,
    EPOS2_TM_T90 = 13,
    EPOS2_TM_T90KP = 14,
    EPOS2_TM_U220 = 15,
    EPOS2_TM_U330 = 16,
    EPOS2_TM_L90 = 17,
    EPOS2_TM_H6000 = 18,

    SAMSUNG_BUILT_IN = 10001,
    // Non-Epson external ESC/POS printer. Like SAMSUNG_BUILT_IN this is a
    // sentinel above the Epson SDK range (0-18) — it's never passed to the
    // Epson SDK, only used to pick column width + font below.
    SNBC_BTP_S80 = 10002,
  }

  export enum EposCmd {
    clearCommandBuffer = 'clearCommandBuffer',
    addTextLineSpace = 'addTextLineSpace',
    addTextAlign = 'addTextAlign',
    addTextRotate = 'addTextRotate',
    addText = 'addText',
    addTextLang = 'addTextLang',
    addTextFont = 'addTextFont',
    addTextSmooth = 'addTextSmooth',
    addTextDouble = 'addTextDouble',
    addTextSize = 'addTextSize',
    addTextStyle = 'addTextStyle',
    addTextPosition = 'addTextPosition',
    addFeedUnit = 'addFeedUnit',
    addFeedLine = 'addFeedLine',
    addImage = 'addImage',
    addLogo = 'addLogo',
    addBarcode = 'addBarcode',
    addSymbol = 'addSymbol',
    addHLine = 'addHLine',
    addVLineBegin = 'addVLineBegin',
    addVLineEnd = 'addVLineEnd',
    addPageBegin = 'addPageBegin',
    addPageEnd = 'addPageEnd',
    addPageArea = 'addPageArea',
    addPageDirection = 'addPageDirection',
    addPagePosition = 'addPagePosition',
    addPageLine = 'addPageLine',
    addPageRectangle = 'addPageRectangle',
    addCut = 'addCut',
    addPulse = 'addPulse',
    addSound = 'addSound',
    addFeedPosition = 'addFeedPosition',
    addLayout = 'addLayout',
    addCommand = 'addCommand',
    addRepeatedText = 'addRepeatedText',
  }
}

export { PrintJob };
